import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Coins, Zap, TrendingUp, Briefcase, AlertTriangle, Loader2, Check, Sparkles, Receipt, Settings2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Credits() {
  const [data, setData] = useState(null);
  const [pkgs, setPkgs] = useState(null);
  const [buying, setBuying] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [limit, setLimit] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/credits/dashboard").then((r) => { setData(r.data); setLimit(String(r.data.spend_limit || "")); });
    api.get("/credits/packages").then((r) => setPkgs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let tries = 0;
    const poll = async () => {
      try {
        const { data: s } = await api.get(`/credits/status/${sid}`);
        if (s.payment_status === "paid") { if (s.credited) toast.success(`${s.credits_purchased} credits added! 🎉`); setVerifying(false); params.delete("session_id"); setParams(params, { replace: true }); load(); return; }
        if (s.status === "expired" || tries > 6) { setVerifying(false); toast.error("Payment not completed."); params.delete("session_id"); setParams(params, { replace: true }); return; }
      } catch { setVerifying(false); return; }
      tries++; setTimeout(poll, 1800);
    };
    poll();
  }, [params, setParams, load]);

  const buy = async (id) => {
    setBuying(id);
    try { const { data: r } = await api.post("/credits/checkout", { package_id: id, origin_url: window.location.origin }); window.location.href = r.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBuying(""); }
  };

  const saveSettings = async (patch) => {
    try { await api.post("/credits/settings", patch); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data || !pkgs) return <Spinner />;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="credits-page">
      <PageHeader title="Credits & Usage" icon={Coins} subtitle="Credits power every AI action. Track usage, top up instantly and never lose momentum." />

      {verifying && <div className="mb-4 rounded-xl bg-[hsl(var(--primary))]/10 p-3 text-sm flex items-center gap-2" data-testid="credits-verifying"><Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…</div>}
      {data.low_credit && (
        <div className="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-sm flex items-center gap-2 text-amber-600" data-testid="low-credit-warning">
          <AlertTriangle className="w-4 h-4" /> You're low on credits ({data.credits_remaining} left). Top up below to keep creating.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat icon={Coins} label="Credits remaining" value={data.credits_remaining.toLocaleString()} accent="#10b981" testid="stat-remaining" />
        <Stat icon={Zap} label="Used today" value={data.today_used.toLocaleString()} accent="#3b9eff" testid="stat-today" />
        <Stat icon={TrendingUp} label="Used this month" value={data.month_used.toLocaleString()} accent="#a855f7" testid="stat-month" />
        <Stat icon={Briefcase} label="Est. jobs left" value={data.est_jobs_remaining.toLocaleString()} accent="#f59e0b" testid="stat-jobs" />
      </div>

      <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Top up credits</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="credit-packages">
        {Object.entries(pkgs.packages).map(([id, p]) => (
          <div key={id} className={`rounded-2xl border p-5 flex flex-col card-hover relative ${p.popular ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/30" : "border-[hsl(var(--border))]"} bg-[hsl(var(--card))]`} data-testid={`package-${id}`}>
            {p.popular && <Badge className="absolute -top-2 right-3 bg-[hsl(var(--primary))]">{p.tagline}</Badge>}
            <div className="font-display font-bold text-lg">{p.name}</div>
            <div className="text-3xl font-extrabold mt-1">{(p.credits + p.bonus).toLocaleString()}<span className="text-sm font-semibold text-[hsl(var(--muted-foreground))]"> cr</span></div>
            {p.bonus > 0 && <div className="text-xs text-emerald-500 font-semibold">+{p.bonus.toLocaleString()} bonus credits</div>}
            <div className="text-2xl font-bold mt-3">${p.price.toFixed(2)}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{!p.popular ? p.tagline : "One-time purchase"}</div>
            <Button onClick={() => buy(id)} disabled={!!buying} className="w-full rounded-full mt-auto bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid={`buy-${id}`}>
              {buying === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Buy now
            </Button>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 mb-8 flex items-center justify-between flex-wrap gap-2" data-testid="enterprise-card">
        <div><div className="font-bold">{pkgs.enterprise.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{pkgs.enterprise.tagline}</div></div>
        <Button variant="outline" className="rounded-full" onClick={() => toast.success("Our team will reach out about Enterprise pricing!")}>Contact sales</Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="credit-settings">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Settings2 className="w-5 h-5" /> Spending controls</h3>
          <div className="flex items-center justify-between py-2">
            <div><div className="font-semibold text-sm">Auto top-up</div><div className="text-xs text-[hsl(var(--muted-foreground))]">Buy more credits automatically when you run low.</div></div>
            <Switch checked={!!data.auto_topup} onCheckedChange={(v) => saveSettings({ auto_topup: v })} data-testid="auto-topup-switch" />
          </div>
          <div className="flex items-center justify-between py-2 gap-3">
            <div><div className="font-semibold text-sm">Spending limit (credits / month)</div><div className="text-xs text-[hsl(var(--muted-foreground))]">0 = no limit.</div></div>
            <div className="flex gap-2">
              <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="w-24" data-testid="spend-limit-input" />
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => saveSettings({ spend_limit: Number(limit) || 0 })} data-testid="save-spend-limit">Save</Button>
            </div>
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Avg cost / game: <b>{data.avg_cost_per_game}</b> cr · Lifetime used: <b>{data.lifetime_used.toLocaleString()}</b> cr</div>
        </div>

        {/* Top features + transactions */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Top credit consumers</h3>
          {data.top_features.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No usage yet — start creating!</p> :
            data.top_features.map((f) => (
              <div key={f.feature} className="flex justify-between text-sm py-1 capitalize" data-testid={`feature-${f.feature}`}><span>{f.feature.replace(/_/g, " ")} <span className="text-[hsl(var(--muted-foreground))]">×{f.count}</span></span><span className="font-bold">{f.credits} cr</span></div>
            ))}
          <h3 className="font-display font-bold mt-4 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4" /> Recent transactions</h3>
          {data.transactions.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="no-transactions">No credit transactions yet.</p> :
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="credit-transactions">
              {data.transactions.slice(0, 6).map((t) => (
                <div key={t.id} className="flex justify-between items-center py-2 text-sm">
                  <div><div className="font-medium capitalize">{t.type} <span className="text-xs text-[hsl(var(--muted-foreground))]">{t.reason}</span></div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">{new Date(t.created_at).toLocaleDateString()}</div></div>
                  <span className={`font-bold ${t.amount >= 0 ? "text-emerald-500" : "text-red-500"}`}>{t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={testid}>
    <div className="w-9 h-9 rounded-xl grid place-items-center mb-2" style={{ background: `${accent}22` }}><Icon className="w-5 h-5" style={{ color: accent }} /></div>
    <div className="font-display text-2xl font-extrabold">{value}</div>
    <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
  </div>
);
